import { useEffect, useState, type FormEvent } from 'react'
import { Button, Field, Input, LogoMark, Notice } from '../design/index.js'
import { useApi } from './api.js'

interface Branding {
  readonly companyName: string
  readonly logo: string | null
}

/**
 * Sign in. Deliberately plain: one card on the grey ground, no marketing,
 * nothing to read before you can work.
 *
 * The company's own logo and name are fetched separately and unauthenticated —
 * that is what a sign-in page is (docs/11 §Branding). The form renders
 * immediately and the branding drops in when it arrives, so a slow or missing
 * answer never stops somebody signing in.
 */
export const LoginPage = ({ onSignedIn }: { onSignedIn: () => void }) => {
  const api = useApi()
  const [branding, setBranding] = useState<Branding | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let live = true
    fetch('/api/public/branding', { headers: { accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((body: Branding | null) => { if (live && body) setBranding(body) })
      .catch(() => undefined)
    return () => { live = false }
  }, [])

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.login(email.trim(), password)
      onSignedIn()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth">
      <form onSubmit={submit} className="auth__card">
        <div className="auth__brand">
          <LogoMark size={36} />
          <span className="brand__word">works<span className="brand__bq">bq</span></span>
        </div>
        {branding?.logo ? (
          <img className="auth__logo" src={branding.logo} alt={branding.companyName || 'Company logo'} />
        ) : null}
        <h1 className="auth__title">{branding?.companyName ? `Sign in to ${branding.companyName}` : 'Sign in'}</h1>
        <p className="auth__lede">Use the email address your administrator set up for you.</p>

        {error ? <Notice kind="bad">{error}</Notice> : null}

        <Field label="Email address" required>
          <Input value={email} onChange={setEmail} type="email" autoComplete="username" autoFocus />
        </Field>
        <Field label="Password" required>
          <Input value={password} onChange={setPassword} type="password" autoComplete="current-password" />
        </Field>

        <div className="auth__submit">
          <Button kind="primary" type="submit" disabled={busy || email === '' || password === ''}>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        </div>
      </form>
    </div>
  )
}
